import { useState, useRef, type ReactNode } from "react";
import { cn } from "../lib/utils";

export interface NavItem {
  label: string;
  href?: string;
  children?: NavItem[];
  description?: string;
  icon?: ReactNode;
}

interface NavigationMenuProps {
  items: NavItem[];
  className?: string;
}

function NavDropdown({ items }: { items: NavItem[] }) {
  return (
    <div className="nav-dropdown" role="menu">
      {items.map(item => (
        <a
          key={item.label}
          href={item.href ?? "#"}
          className="nav-dropdown-item"
          role="menuitem"
          onClick={e => e.preventDefault()}
        >
          {item.icon && <span className="nav-dropdown-icon">{item.icon}</span>}
          <div className="nav-dropdown-text">
            <span className="nav-dropdown-label">{item.label}</span>
            {item.description && <span className="nav-dropdown-desc">{item.description}</span>}
          </div>
        </a>
      ))}
    </div>
  );
}

export function NavigationMenu({ items, className }: NavigationMenuProps) {
  const [open, setOpen] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const handleEnter = (label: string) => {
    clearTimeout(closeTimer.current);
    setOpen(label);
  };

  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(null), 80);
  };

  return (
    <nav className={cn("nav-menu", className)} role="navigation" aria-label="Main navigation">
      <div className="nav-menu-list">
        {items.map(item => (
          <div
            key={item.label}
            className="nav-menu-item"
            onMouseEnter={() => item.children ? handleEnter(item.label) : undefined}
            onMouseLeave={handleLeave}
          >
            <a
              href={item.href ?? "#"}
              className={cn("nav-menu-trigger", open === item.label && "nav-menu-trigger-active")}
              onClick={e => e.preventDefault()}
              aria-expanded={item.children ? open === item.label : undefined}
              aria-haspopup={item.children ? "menu" : undefined}
            >
              {item.label}
              {item.children && (
                <span className={cn("nav-menu-chevron", open === item.label && "nav-menu-chevron-open")}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
                </span>
              )}
            </a>
            {item.children && open === item.label && (
              <NavDropdown items={item.children} />
            )}
          </div>
        ))}
      </div>
    </nav>
  );
}
